import React, { forwardRef } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const GeneCoverageChart = forwardRef(({ data, ref, runId }) => {
  if (!data || !data.regions) {
    return <p>Loading...</p>
  }
  const labels = ['Regions']

  const maxDepth = Math.max(...data.regions.map(r => r.depth))
  const minDepth = Math.min(...data.regions.map(r => r.depth))

  const datasets = data.regions.map((region, index) => {
    const intensity = (region.depth - minDepth) / (maxDepth - minDepth)

    const backgroundColor = data.regions.length > 1
      ? region.exon
        ? region.color
        : `rgba(107, 114, 128, ${0.3 + intensity * 0.7})`
      : region.exon
        ? region.color
        : '#6B7280'
    const borderWidth = 0.5
    const borderColor = `rgba(0, 0, 0, .8)`
    return {
      label: `${region.chrom}:${region.start}-${region.end}`,
      data: [region.end - region.start],
      backgroundColor,
      exon: region.exon,
      depth: region.depth,
      borderColor,
      borderWidth,
      url: `https://app.gennext.bio/igv/${runId}&${region.chrom}:${region.start}-${region.end}&snv`
    }
  })
  const chartData = {
    labels,
    datasets,
  }
  const barThickness = 50;
  const canvasHeight = barThickness + barThickness + 40;

  const options = {
    responsive: true,
    indexAxis: 'y',
    maintainAspectRatio: false,
    barThickness: barThickness,
    onClick: (event, elements, chart) => {
      if (elements.length > 0) {
        const datasetIndex = elements[0].datasetIndex;
        const dataset = chart.data.datasets[datasetIndex];
        const url = dataset.url;
        if (url) {
          window.open(url, '_blank');
        }
      }
    },
    layout: {
      padding: {
        left: 15,
        right: 10,
      }
    },
    elements: {
      bar: {
        clip: false,
        borderSkipped: false,
      },
    },

    plugins: {
      legend: {
        display: true,
        position: 'bottom', // önce alta al
        align: 'start',     // sonra sola hizala
        labels: {
          generateLabels: function (chart) {
            const datasets = chart.data.datasets;

            const exonicRegions = datasets
              .filter(ds => ds.exon && ds.backgroundColor.includes('rgba(0, 123, 255'))
              .map(ds => ({
                color: ds.backgroundColor,
                depth: ds.depth
              }));

            const sorted = exonicRegions.sort((a, b) => a.depth - b.depth);

            const unique = [];
            const seen = new Set();
            for (const item of sorted) {
              if (!seen.has(item.color)) {
                unique.push(item);
                seen.add(item.color);
              }
            }

            const legendItems = [];

            if (unique.length === 1) {
              legendItems.push({
                text: `${unique[0].depth}`,
                fillStyle: unique[0].color,
                strokeStyle: '#000',
                lineWidth: 1
              });
            } else if (unique.length === 2) {
              legendItems.push({
                text: ` ${unique[0].depth}`,
                fillStyle: unique[0].color,
                strokeStyle: '#000',
                lineWidth: 1
              });
              legendItems.push({
                text: `${unique[1].depth}`,
                fillStyle: unique[1].color,
                strokeStyle: '#000',
                lineWidth: 1
              });
            } else if (unique.length >= 3) {
              const low = unique[0];
              const mid = unique[Math.floor(unique.length / 2)];
              const high = unique[unique.length - 1];

              legendItems.push({
                text: `Depth: ${low.depth}`,
                fillStyle: low.color,
                strokeStyle: '#000',
                lineWidth: 1
              });
              legendItems.push({
                text: ` Depth: ${mid.depth}`,
                fillStyle: mid.color,
                strokeStyle: '#000',
                lineWidth: 1
              });
              legendItems.push({
                text: `Depth: ${high.depth}`,
                fillStyle: high.color,
                strokeStyle: '#000',
                lineWidth: 1
              });
            }

            const hasGray = datasets.some(ds =>
              !ds.exon &&
              (ds.backgroundColor.includes('107, 114, 128') || ds.backgroundColor === '#6B7280')
            );
            if (hasGray) {
              legendItems.push({
                text: 'Non-exonic',
                fillStyle: 'rgba(107, 114, 128, 0.6)',
                strokeStyle: '#000',
                lineWidth: 1
              });
            }

            return legendItems;
          }

        }
      },
      title: {
        display: true,
        text: `Regions Plot for ${data.geneName}`,
        align: 'start',
        color: '#0F172A',
        font: {
          size: 18,
          weight: 'bold',
        },
      },
      tooltip: {
        mode: 'nearest',
        intersect: true,
        position: 'nearest',
        yAlign: 'bottom',
        xAlign: 'center',
        displayColors: false,
        callbacks: {
          title: function () {
            return '';
          },
          label: function (context) {
            const regionLabel = context.dataset.label

            const regionLength = context.raw
            const regionExon = context.dataset.exon
            const regionDepth = context.dataset.depth
            return [`Region: ${regionLabel}`, `Length ${regionLength}`, `Depth: ${regionDepth}`, regionExon ? `Exon: ${regionExon}` : '',];
          },
        },
        titleFont: {
          weight: 'normal',
        },
        bodyFont: {
          weight: 'bold',
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: false,
          text: 'Mean Depth',
        },
        border: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        }
      },
      y: {
        stacked: true,
        title: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        }
      },
    },
  }

  return <div style={{ height: `${canvasHeight}px` }}>
    <Bar ref={ref} data={chartData} options={options} />
  </div>
});

export default GeneCoverageChart
